import {
  Drawer,
  notification,
  DatePicker,
  Select,
  InputNumber,
  Input,
  Button,
  Modal,
  Tooltip,
} from "antd";
import { FC, useEffect, useState } from "react";
import moment from "moment";
import { APIS } from "../../../constants/apis";
import DayTypeLegend from "../../DayTypeLegend";
import {
  villaDayClass,
  villaDayIndex,
  specialDayLabel,
  EMPTY_VILLA_DAYS,
  VillaDayIndex,
} from "../../../utils/dayTypes";
import API from "../../../services/api.service";
import { formatAMPM, formatDateMonthYear } from "../../../utils/helper";
import style from "./style.module.scss";
import { useAuth } from "../../../store/context/login";

const { RangePicker } = DatePicker;
const { Option } = Select;

export interface bookingdrawer {
  data?: any;
  showDrawer: any;
  setShowDrawer: Function;
  onUpdate: Function;
  param?: any;
}
const BookingDrawerComponent: FC<bookingdrawer> = ({
  data,
  showDrawer,
  setShowDrawer,
  onUpdate,
  param,
}) => {
  // Edit / cancel are staff-only. An owner signing into the panel still sees the
  // booking's details, just without the actions.
  const { isManager } = useAuth();
  const [visible, setVisible] = useState(showDrawer);
  const [response, setResponse] = useState({
    res: "",
    id: 0,
    status: 1,
  });
  let guestArray: Array<any> = [];

  const [edit, setEdit] = useState(false);

  // ----- booking edit / cancel (admin) -----
  const [editMode, setEditMode] = useState(false);
  const [saving, setSaving] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [cancelOpen, setCancelOpen] = useState(false);
  const [cancelReason, setCancelReason] = useState<string>("");
  const [villas, setVillas] = useState<any[]>([]);
  const [fVilla, setFVilla] = useState<number | undefined>(undefined);
  const [fStart, setFStart] = useState<string>("");
  const [fEnd, setFEnd] = useState<string>("");
  const [fBookingFor, setFBookingFor] = useState<string>("myself");
  const [fName, setFName] = useState<string>("");
  const [fMobile, setFMobile] = useState<string>("");
  const [fEmail, setFEmail] = useState<string>("");
  const [fAdults, setFAdults] = useState<number>(1);
  const [fKids, setFKids] = useState<number>(0);
  const [fSeniors, setFSeniors] = useState<number>(0);
  const [fInfants, setFInfants] = useState<number>(0);
  const [fPets, setFPets] = useState<number>(0);
  const [fNote, setFNote] = useState<string>("");
  // dates unavailable for the assigned villa while editing (kept separate so the
  // hover tooltip can say WHY a day is blocked)
  const [bookedSet, setBookedSet] = useState<Set<string>>(new Set());
  const [maintSet, setMaintSet] = useState<Set<string>>(new Set());

  // villa list for the "assigned villa" picker
  useEffect(() => {
    (async () => {
      try {
        const res = await API.post(APIS.HOME.PROPERTY_NAME_LIST, {
          role: "MANAGER",
        });
        if (res.data.status === 1) setVillas(res.data.data || []);
      } catch (e) {
        // silent
      }
    })();
  }, []);

  // reset edit mode whenever a different booking is opened
  useEffect(() => {
    setEditMode(false);
  }, [data?.id]);

  // While editing, pull the villa's booked + maintenance days so we can grey them
  // out in the date picker. The booking being edited is excluded from "booked" so
  // the owner can still keep / extend its own range.
  useEffect(() => {
    if (!editMode || !fVilla) {
      setBookedSet(new Set());
      setMaintSet(new Set());
      setVillaDays(EMPTY_VILLA_DAYS);
      return;
    }
    (async () => {
      try {
        const res = await API.get(APIS.BOOKING.CALENDAR + fVilla);
        const d = res.data?.data || {};
        const toDay = (s: string) => String(s).slice(0, 10); // YYYY-MM-DD
        setVillaDays(villaDayIndex(d));
        const own = new Set<string>();
        if (data?.start_date && data?.end_date) {
          let c = moment(data.start_date);
          const e = moment(data.end_date);
          while (c.isSameOrBefore(e, "day")) {
            own.add(c.format("YYYY-MM-DD"));
            c = c.clone().add(1, "day");
          }
        }
        setBookedSet(
          new Set<string>(
            (d.booked_date || []).map(toDay).filter((x: string) => !own.has(x))
          )
        );
        setMaintSet(new Set<string>((d.maintenanceday_dates || []).map(toDay)));
      } catch (e) {
        setBookedSet(new Set());
        setMaintSet(new Set());
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editMode, fVilla, data?.id]);

  // Un-pickable: any past day, or a day the villa is already booked / under
  // maintenance (its own booking's dates are excluded above).
  const editDisabledDate = (current: any) => {
    if (!current) return false;
    if (current < moment().startOf("day")) return true;
    const ds = current.format("YYYY-MM-DD");
    return bookedSet.has(ds) || maintSet.has(ds);
  };

  // Black out booked / maintenance days and show a hover tooltip explaining why.
  // Peak / high-peak come from the assigned villa's own calendar, so the
  // long-weekend and 14-day-decay rules match the app exactly.
  const [villaDays, setVillaDays] = useState<VillaDayIndex>(EMPTY_VILLA_DAYS);

  // Booked / maintenance block the date and win the colour; peak and high peak
  // stay selectable and are shown for rate context only.
  const editDateRender = (current: any) => {
    const ds = current.format("YYYY-MM-DD");
    const isMaint = maintSet.has(ds);
    const isBooked = bookedSet.has(ds);
    const special = villaDayClass(current, villaDays);
    const cls = isMaint ? "cell_maint" : isBooked ? "cell_booked" : special;
    const inner = (
      <div className={`ant-picker-cell-inner ${cls}`}>{current.date()}</div>
    );
    const title = isMaint
      ? "Maintenance day"
      : isBooked
      ? "Booked"
      : specialDayLabel(special);
    return title ? <Tooltip title={title}>{inner}</Tooltip> : inner;
  };

  // var months = ["Jan", "Feb", "Mar", "Apr", "May", "June", "July", "Aug", "Sept", "Oct", "Nov", "Dec"];
  let dateNow = new Date();
  let dateEnd = new Date();
  let start = new Date();
  let end = new Date();
  let checkinTime = new Date();
  let total_members = 0;

  if (!!data) {
    data.bookingRequest = data.details.special;
    data.guest = data.details.guest;
    dateNow = new Date(data.start_date);
    dateEnd = new Date(data.end_date);
    start = new Date(data.start_date);
    end = new Date(data.end_date);
    checkinTime = new Date("2022-01-01 " + data.start_time);
    total_members =
      data.guest.adults_count +
      data.guest.kids_count +
      data.guest.seniorcitizen_count +
      data.guest.infants_count;
    Object.keys(data.guest).map((value, index) => {
      let lastIndx, fielValue;
      if (
        data.guest[value] > 0 &&
        (value.includes("_count") || value.includes("s_count"))
      ) {
        if (value.includes("_count")) {
          lastIndx = value.indexOf("_count");
        }
        if (value.includes("s_count")) {
          lastIndx = value.indexOf("s_count");
        }
        fielValue = value.substring(0, lastIndx);
        guestArray.push({ field: fielValue, count: data.guest[value] });
      }
    });
  }

  const getDayNight = (date_1: any, date_2: any) => {
    let difference = date_1.getTime() - date_2.getTime();
    let TotalDays = Math.ceil(difference / (1000 * 3600 * 24));

    return TotalDays + 1;
  };

  const addResponse = async () => {
    const res = await API.post(
      APIS.PROPERTY.UPDATE_REQUEST + data.property_id,
      {
        id: response.id,
        response: response.res,
        status: response.status,
      },
    );
    if (res.data.status === 1) {
      onUpdate(param);
      setShowDrawer(false);
      setVisible(false);
      setEdit(false);
    } else {
      notification.info({
        message: `${res.data.message}`,
        placement: "top",
      });
    }
  };

  const onClose = () => {
    setVisible(false);
    setShowDrawer(false);
    setEdit(false);
    setEditMode(false);
  };
  useEffect(() => {
    setVisible(showDrawer);
  }, [showDrawer]);

  // ----- open the edit form, pre-filled from the current booking -----
  const openEdit = () => {
    setFVilla(data.property_id);
    setFStart(moment(data.start_date).format("YYYY-MM-DD"));
    setFEnd(moment(data.end_date).format("YYYY-MM-DD"));
    setFBookingFor(data.booking_for === "MYSELF" ? "myself" : "guest");
    setFName(data.guest?.name || "");
    setFMobile(data.guest?.mobile || "");
    setFEmail(data.guest?.email || "");
    setFAdults(data.guest?.adults_count || 1);
    setFKids(data.guest?.kids_count || 0);
    setFSeniors(data.guest?.seniorcitizen_count || 0);
    setFInfants(data.guest?.infants_count || 0);
    setFPets(data.guest?.pets_count || 0);
    setFNote(data.note || "");
    setEditMode(true);
  };

  // ----- save the edit (existing booking-details-edit endpoint) -----
  const onSave = async () => {
    if (!fVilla || !fStart || !fEnd) {
      notification.error({
        message: "Villa and dates are required",
        placement: "top",
      });
      return;
    }
    const body: any = {
      property_id: fVilla,
      booking_id: data.id,
      start_date: fStart,
      start_time: data.start_time || "12:00:00",
      end_date: fEnd,
      end_time: "11:00:00",
      booking_for: fBookingFor,
      member: {
        adults_count: fAdults,
        kids_count: fKids,
        infants_count: fInfants,
        seniorcitizen_count: fSeniors,
        pets_count: fPets,
      },
      // optional note for the owner (blank clears it); shown in the app
      note: fNote,
      // admin-panel action -> manager-gated; backend validates availability only
      // (clash + maintenance), not the owner booking rules
      enforce_rules: true,
    };
    if (fBookingFor === "guest") {
      body.guest = {
        name: fName,
        email: fEmail,
        mobile: fMobile,
        govt_idtype: data.guest?.govt_idtype || "",
      };
    }
    try {
      setSaving(true);
      const res = await API.post(APIS.BOOKING.EDIT, body);
      if (res.data.status === 1) {
        notification.success({
          message: "Booking updated",
          placement: "top",
        });
        setEditMode(false);
        onUpdate(param);
        setShowDrawer(false);
        setVisible(false);
      } else {
        // backend validation (clash / rule violation) comes back here
        notification.error({
          message:
            res.data.data?.message ||
            res.data.message ||
            "Could not update booking",
          placement: "top",
        });
      }
    } catch (e: any) {
      notification.error({
        message:
          e?.response?.data?.data?.message ||
          e?.response?.data?.message ||
          "Something went wrong",
        placement: "top",
      });
    } finally {
      setSaving(false);
    }
  };

  // ----- cancel the booking (existing cancel-booking endpoint) -----
  const onCancelBooking = async () => {
    const reason = cancelReason.trim();
    if (!reason) {
      notification.error({
        message: "Please enter a reason for cancelling",
        placement: "top",
      });
      return;
    }
    try {
      setCancelling(true);
      const res = await API.post(APIS.BOOKING.CANCEL, {
        booking_id: data.id,
        enforce_rules: true, // admin-panel action -> manager-only on the backend
        reason,
      });
      const inner = res.data?.data;
      if (res.data.status === 1 && inner?.status !== "0") {
        notification.success({
          message: "Booking cancelled",
          placement: "top",
        });
        setCancelOpen(false);
        setCancelReason("");
        onUpdate(param);
        setShowDrawer(false);
        setVisible(false);
      } else {
        notification.error({
          message: inner?.message || "Could not cancel booking",
          placement: "top",
        });
      }
    } catch (e: any) {
      notification.error({
        message:
          e?.response?.data?.data?.message ||
          e?.response?.data?.message ||
          "Something went wrong",
        placement: "top",
      });
    } finally {
      setCancelling(false);
    }
  };

  const lbl: any = {
    display: "block",
    fontSize: 12,
    fontWeight: 600,
    letterSpacing: "0.04em",
    textTransform: "uppercase",
    color: "#6b7a72",
    margin: "0 0 6px",
  };
  const field: any = { marginBottom: 16 };

  // Past bookings (already checked out) can't be edited or cancelled, so the
  // admin Edit / Cancel actions are hidden for them.
  const startOfToday = new Date();
  startOfToday.setHours(0, 0, 0, 0);
  const isPast = !!data && end < startOfToday;

  return (
    <>
      <Drawer
        placement="right"
        onClose={onClose}
        closeIcon={<i className="icon-previous"></i>}
        visible={visible}
        closable={false}
        width={514}
        bodyStyle={{
          padding: 0,
          position: "relative",
          overflow: "auto",
        }}
        // The edit actions live in the Drawer footer, which renders OUTSIDE the
        // scrolling body — so Save changes stays visible at any zoom level.
        footer={
          isManager && editMode && !!data ? (
            <div style={{ display: "flex", gap: 12 }}>
              <Button
                type="primary"
                loading={saving}
                onClick={onSave}
                style={{ background: "#082017", borderColor: "#082017" }}
              >
                Save changes
              </Button>
              <Button onClick={() => setEditMode(false)}>Cancel</Button>
            </div>
          ) : null
        }
        footerStyle={{ padding: 16, background: "#fff" }}
        className={"ant-drawer-mask-container"}
      >
        {!!data && (
          <>
            <div className={style.drawer_header}>
              <div className={style.close_btn} onClick={onClose}>
                <i className="icon-previous"></i>
              </div>
              <div className={style.title}>{`${formatDateMonthYear(
                dateNow,
              )} - ${formatDateMonthYear(dateEnd)}`}</div>
            </div>

            {/* ===== EDIT MODE ===== */}
            {isManager && editMode ? (
              <div className="drawer">
                <div style={field}>
                  <label style={lbl}>Assigned villa</label>
                  <Select
                    style={{ width: "100%" }}
                    value={fVilla}
                    placeholder="Select villa"
                    disabled
                    onChange={(v) => setFVilla(v)}
                  >
                    {villas.map((p: any) => (
                      <Option key={p.id} value={p.id}>
                        {p.title}
                      </Option>
                    ))}
                  </Select>
                </div>

                <div style={field}>
                  <label style={lbl}>Dates</label>
                  <DayTypeLegend />
                  <RangePicker
                    style={{ width: "100%" }}
                    dropdownClassName="range_fit_drawer"
                    getPopupContainer={(trigger: any) => trigger.parentElement}
                    disabledDate={editDisabledDate}
                    dateRender={editDateRender}
                    value={
                      fStart && fEnd ? [moment(fStart), moment(fEnd)] : null
                    }
                    onChange={(_: any, ds: any) => {
                      setFStart(ds ? ds[0] : "");
                      setFEnd(ds ? ds[1] : "");
                    }}
                    format="YYYY-MM-DD"
                  />
                </div>

                <div style={field}>
                  <label style={lbl}>Booking for</label>
                  <Select
                    style={{ width: "100%" }}
                    value={fBookingFor}
                    disabled
                    onChange={(v) => setFBookingFor(v)}
                  >
                    <Option value="myself">Myself (owner)</Option>
                    <Option value="guest">Guest</Option>
                  </Select>
                </div>

                {fBookingFor === "guest" && (
                  <>
                    <div style={field}>
                      <label style={lbl}>Guest name</label>
                      <Input
                        value={fName}
                        disabled
                        onChange={(e) => setFName(e.target.value)}
                      />
                    </div>
                    <div style={{ display: "flex", gap: 12 }}>
                      <div style={{ ...field, flex: 1 }}>
                        <label style={lbl}>Mobile</label>
                        <Input
                          value={fMobile}
                          disabled
                          onChange={(e) => setFMobile(e.target.value)}
                        />
                      </div>
                      <div style={{ ...field, flex: 1 }}>
                        <label style={lbl}>Email</label>
                        <Input
                          value={fEmail}
                          disabled
                          onChange={(e) => setFEmail(e.target.value)}
                        />
                      </div>
                    </div>
                  </>
                )}

                <label style={lbl}>Guests</label>
                <div
                  style={{
                    display: "flex",
                    flexWrap: "wrap",
                    gap: 12,
                    marginBottom: 8,
                  }}
                >
                  {[
                    ["Adults", fAdults, setFAdults, 1],
                    ["Kids", fKids, setFKids, 0],
                    ["Seniors", fSeniors, setFSeniors, 0],
                    ["Infants", fInfants, setFInfants, 0],
                    ["Pets", fPets, setFPets, 0],
                  ].map(([label, val, setter, min]: any) => (
                    <div key={label} style={{ width: 88 }}>
                      <label style={{ ...lbl, textTransform: "none" }}>
                        {label}
                      </label>
                      <InputNumber
                        style={{ width: "100%" }}
                        min={min}
                        value={val}
                        disabled
                        onChange={(v) => setter(Number(v) || 0)}
                      />
                    </div>
                  ))}
                </div>

                <div style={field}>
                  <label style={lbl}>Note (optional)</label>
                  <Input.TextArea
                    rows={3}
                    value={fNote}
                    placeholder="Add a note for the owner — visible in the app"
                    onChange={(e) => setFNote(e.target.value)}
                  />
                </div>

              </div>
            ) : (
              /* ===== VIEW MODE ===== */
              <div className="drawer">
                {/* admin actions */}
                {isManager && data.status === 1 && !isPast && (
                  <div
                    style={{
                      display: "flex",
                      gap: 12,
                      marginBottom: 20,
                    }}
                  >
                    <Button
                      type="primary"
                      onClick={openEdit}
                      style={{
                        background: "#082017",
                        borderColor: "#082017",
                      }}
                    >
                      Edit booking
                    </Button>
                    <Button
                      danger
                      onClick={() => {
                        setCancelReason("");
                        setCancelOpen(true);
                      }}
                    >
                      Cancel booking
                    </Button>
                    <Modal
                      visible={cancelOpen}
                      onCancel={() => setCancelOpen(false)}
                      footer={null}
                      destroyOnClose
                    >
                      <div
                        style={{
                          fontSize: 18,
                          fontWeight: 700,
                          marginBottom: 12,
                        }}
                      >
                        Cancel booking
                      </div>
                      <p style={{ marginBottom: 12, color: "#4b5563" }}>
                        The held dates will be released and the owner will be
                        notified. This can’t be undone.
                      </p>
                      <label style={lbl}>Reason for cancelling *</label>
                      <Input.TextArea
                        rows={3}
                        value={cancelReason}
                        onChange={(e) => setCancelReason(e.target.value)}
                        placeholder="e.g. Owner requested, double booking, maintenance clash…"
                      />
                      <div
                        style={{
                          display: "flex",
                          justifyContent: "flex-end",
                          gap: 10,
                          marginTop: 20,
                        }}
                      >
                        <Button onClick={() => setCancelOpen(false)}>
                          Keep booking
                        </Button>
                        <Button
                          danger
                          type="primary"
                          loading={cancelling}
                          onClick={onCancelBooking}
                        >
                          Cancel booking
                        </Button>
                      </div>
                    </Modal>
                  </div>
                )}
                {data.status === 0 && (
                  <div
                    style={{
                      marginBottom: 16,
                      color: "#c0392b",
                      fontWeight: 600,
                    }}
                  >
                    This booking is cancelled.
                  </div>
                )}

                <div className="drawer_address">
                  <div className="address">{data.propertyDetails.title}</div>
                  <div className="sub_address">
                    {data.propertyDetails.location}, {data.propertyDetails.city}
                  </div>
                </div>
                <hr className="line" />
                <div className="property_data">
                  <div className="property_booking">Property booking for</div>
                  <div className="booking_name">
                    {data.guest.name}{" "}
                    {data.booking_for !== "MYSELF" ? <>&#40;Guest&#41;</> : " "}
                    {total_members - 1 > 0
                      ? total_members - 1 == 1
                        ? "+ " + (total_members - 1) + " Member"
                        : "+ " + (total_members - 1) + " Members"
                      : ""}{" "}
                  </div>
                  <div className="total_guest">
                    {guestArray.length > 2 &&
                      guestArray.map((value: any, index: any) => {
                        return index !== guestArray.length - 1
                          ? `${value.count} ${value.field}, `
                          : index === 0
                          ? `${value.count} ${value.field}`
                          : `and ${value.count} ${value.field}`;
                      })}
                    {guestArray.length <= 2 &&
                      guestArray.length > 0 &&
                      guestArray.map((value: any, index: any) => {
                        return index !== guestArray.length - 1
                          ? `${value.count} ${
                              value.count > 1 ? value.field + "s" : value.field
                            } `
                          : index === 0
                          ? `${value.count} ${
                              value.count > 1 ? value.field + "s" : value.field
                            }`
                          : `and ${value.count} ${
                              value.count > 1 ? value.field + "s" : value.field
                            }`;
                      })}
                  </div>
                  <div className="booking_date_head">Date & time of Arrival</div>
                  <div className="booking_dates">
                    <div className="start_date">
                      {formatDateMonthYear(dateNow)}
                    </div>
                    <div className="total_stay">
                      &#40; {getDayNight(end, start)} days |{" "}
                      {getDayNight(end, start) - 1} night &#41;{" "}
                    </div>
                    <div className="end_date">
                      {formatDateMonthYear(dateEnd)}
                    </div>
                  </div>
                  <div className="booking_time">at {formatAMPM(checkinTime)}</div>
                </div>
                <hr className="line" />
                {!edit &&
                  data.status !== 0 &&
                  data.details.special &&
                  data.details.special.length <= 0 && (
                    <div className="special_request_head">
                      <div className="special">No request</div>
                    </div>
                  )}
                {!edit &&
                  data.status !== 0 &&
                  data.details.special &&
                  data.details.special.length > 0 &&
                  data.details.special.map((val: any, ind: any) => {
                    return (
                      <div>
                        <div className="special_request_head">
                          <div className="special">Special Request</div>
                          <div className="accepted">
                            {val.status === 0
                              ? "Pending"
                              : val.status === 1
                              ? "Accepted"
                              : "Denied"}
                            <img
                              src={`/images/property/${
                                val.status === 0
                                  ? "pending_icon.svg"
                                  : val.status === 1
                                  ? "accepted_icon.svg"
                                  : "denied_icon.png"
                              }`}
                              alt="accept"
                            ></img>
                          </div>
                        </div>
                        <div className="special_request_content">
                          {val.description}
                        </div>
                        <div className="request_time">
                          at {formatAMPM(new Date(val.created_at))}
                        </div>
                        {val.status !== 0 && (
                          <div className={style.reason}>
                            <div className={style.reason_left}>
                              <div className={style.reason_head}>Reason</div>
                              <div className={style.reason_content}>
                                {val.response}
                              </div>
                              <div className="request_time">
                                at {formatAMPM(new Date(val.created_at))}
                              </div>
                            </div>
                            <div className={style.reason_right}>
                              {data && data.status === 1 && (
                                <div
                                  className={style.edit_btn}
                                  onClick={() => {
                                    setResponse({
                                      ...response,
                                      res: val.response,
                                      id: val.id,
                                      status: val.status,
                                    });
                                    setEdit(true);
                                  }}
                                >
                                  Edit
                                </div>
                              )}
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}

                {data && data.status === 1 && edit && (
                  <>
                    <div className={style.special_request}>Add Response</div>

                    <textarea
                      className={style.textfield}
                      value={response.res}
                      placeholder="Add a comment to let the the property owner know more regarding the complaint "
                      onChange={(e) => {
                        setResponse({
                          ...response,
                          res: e.target.value,
                        });
                      }}
                    />
                  </>
                )}

                {data && data.status === 1 && edit && (
                  <div className={style.buttongroup}>
                    <button
                      className={style.requestbuttons}
                      style={{ marginRight: "25px" }}
                      onClick={onClose}
                    >
                      Cancel
                    </button>
                    <button
                      onClick={() => {
                        addResponse();
                      }}
                      className={style.requestbuttons}
                      style={{ background: "#082017", color: "white" }}
                    >
                      {data.status === 0 ? "Confirm" : "Update"}
                    </button>
                  </div>
                )}
              </div>
            )}
          </>
        )}
      </Drawer>
    </>
  );
};

export default BookingDrawerComponent;
